import React from 'react'

const NAME_PART_ORDER = ['first name', 'middle name', 'last name']

function namePartFromField(fieldName) {
  const n = String(fieldName || '').toLowerCase()
  if (/middle/.test(n)) return 'middle name'
  if (/given|first/.test(n)) return 'first name'
  if (/surname|family|last/.test(n)) return 'last name'
  return null
}

function nameMismatchSentence(parts) {
  const unique = NAME_PART_ORDER.filter((p) => parts.includes(p))
  if (!unique.length) return null
  const list = unique.length === 1
    ? unique[0]
    : unique.length === 2
      ? `${unique[0]} and ${unique[1]}`
      : `${unique.slice(0, -1).join(', ')} and ${unique[unique.length - 1]}`
  return `There seems to be a ${list} mismatch. Please verify it against the ID.`
}

/**
 * greenID status FRAUD on a name field means the name did not match the document.
 * Do not show that as fraud.
 */
export function softenDvsFraudWording(raw) {
  const text = String(raw || '')
  if (!/FRAUD/i.test(text)) return text
  const nameParts = []
  const replaced = text.replace(
    /([A-Za-z0-9_.]+)\s*:\s*FRAUD\b(?:\s*→\s*[^;.]*)?/gi,
    (_full, field) => {
      const part = namePartFromField(field)
      if (!part) return `${field} does not match the document. Please verify`
      if (!nameParts.includes(part)) nameParts.push(part)
      return ''
    }
  )
  const sentence = nameMismatchSentence(nameParts)
  let out = replaced
    .replace(/\s*;\s*(?=;)/g, '')
    .replace(/:\s*;/g, ': ')
    .replace(/\s{2,}/g, ' ')
    .replace(/[:\s;]+$/g, '')
    .trim()
  if (!sentence) return out || text
  if (/\)$/.test(out)) return `${out}: ${sentence}`
  if (!out) return sentence
  return `${out} ${sentence}`.replace(/\s{2,}/g, ' ').trim()
}

/** Strip GBG test-licence boilerplate; keep status or real GreenID hint. */
export function shortenDvsFailureMessage(raw) {
  if (raw == null) return ''
  let s = String(raw).trim()
  if (!s) return ''
  s = softenDvsFraudWording(s)
  s = s
    .replace(/\s*(?:GBG\s+)?test NSW:[\s\S]*$/i, '')
    .replace(/\s*—?\s*check licence and card numbers\.?/gi, '')
    .replace(/\s*—\s*$/g, '')
    .trim()

  const statusLine = s.match(
    /^(?:document verification status|greenID status|DVS status)\s*:\s*([A-Z0-9_]+)\s*(?:[—\-–:]\s*(.*))?$/i
  )
  if (statusLine) {
    const status = statusLine[1].toUpperCase()
    const rest = (statusLine[2] || '').trim()
    if (!rest || /^check\b/i.test(rest)) return status
    return rest.length <= 160 ? rest : status
  }

  const pending = s.match(/^DVS not verified\s*\(([A-Z0-9_]+)\)(?:\s*:\s*(.*))?$/i)
  if (pending) {
    const status = pending[1].toUpperCase()
    const rest = (pending[2] || '').trim()
    if (!rest || /^check\b/i.test(rest)) return status
    return rest.length <= 160 ? rest : status
  }

  return s
}

export function dvsResponseSummary(item) {
  if (!item) return '—'
  const r = item.dvsResult || item.queueDvsResult
  if (r?.verified === true) return 'Verified'
  const raw =
    r?.failure_reason ||
    item.errorMessage ||
    item.queueErrorMessage ||
    r?.message ||
    r?.error ||
    null
  if (raw) return shortenDvsFailureMessage(raw) || 'Not verified'
  if (!r) {
    if (item.dvsCheckedAt || item.queueDvsCheckedAt) return 'No stored response payload'
    return 'Not run yet'
  }
  return r.verified === false ? 'Not verified' : 'See full response'
}

export function effectiveDvsFieldsFromPayout(item) {
  if (!item) return {}
  const ocr = item.queueOcrData || item.ocrData || {}
  return {
    document_type: ocr.document_type || 'drivers_licence',
    document_number: ocr.document_number || '',
    document_card_number: String(
      ocr.document_card_number || ocr.licence_card_number || ocr.rta_number || ''
    ).trim(),
    given_name: ocr.given_name || '',
    middle_name: ocr.middle_name || ocr.middleName || '',
    family_name: ocr.family_name || '',
    date_of_birth: ocr.date_of_birth || '',
    state_of_issue: ocr.state_of_issue || 'NSW',
    country_code: ocr.country_code || 'AU',
    ocr_source: ocr.ocr_source || null,
  }
}

export default function DvsResponsePanel({ item, ocrDataOverride }) {
  const [showRaw, setShowRaw] = React.useState(false)
  if (!item) return null
  const summary = dvsResponseSummary(item)
  const shortError = shortenDvsFailureMessage(item.queueErrorMessage || item.errorMessage)
  const ocrData = ocrDataOverride || item.queueOcrData || item.ocrData
  const hasPayload = item.queueDvsResult || item.dvsResult || item.errorMessage || item.queueErrorMessage || ocrData

  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium text-gray-900">DVS response</div>
          <p className="text-gray-700 mt-1 break-words">{summary}</p>
          {item.queueDvsProvider && (
            <p className="text-xs text-gray-500 mt-1">Provider: {item.queueDvsProvider}</p>
          )}
        </div>
        {item.queueDvsCheckedAt && (
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {new Date(item.queueDvsCheckedAt).toLocaleString()}
          </span>
        )}
      </div>
      {hasPayload && (
        <>
          <button
            type="button"
            className="text-xs text-psg-teal mt-2 underline"
            onClick={() => setShowRaw((v) => !v)}
          >
            {showRaw ? 'Hide' : 'Show'} full JSON
          </button>
          {showRaw && (
            <div className="mt-2 space-y-2">
              {ocrData && (
                <div>
                  <div className="text-xs font-medium text-gray-600 mb-1">DVS document data (OCR / submitted)</div>
                  <pre className="text-xs bg-white border rounded p-2 overflow-auto max-h-40 whitespace-pre-wrap">
                    {JSON.stringify(ocrData, null, 2)}
                  </pre>
                </div>
              )}
              {shortError && (
                <div>
                  <div className="text-xs font-medium text-red-700 mb-1">Error</div>
                  <pre className="text-xs bg-white border border-red-100 rounded p-2 overflow-auto max-h-32 whitespace-pre-wrap text-red-900">
                    {shortError}
                  </pre>
                </div>
              )}
              {(item.queueDvsResult || item.dvsResult) && (
                <div>
                  <div className="text-xs font-medium text-gray-600 mb-1">Provider result</div>
                  <pre className="text-xs bg-white border rounded p-2 overflow-auto max-h-64 whitespace-pre-wrap">
                    {JSON.stringify(item.queueDvsResult || item.dvsResult, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
